import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from './Card';

export interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: 'teal' | 'purple' | 'gray';
  className?: string;
}

export interface LoadingCardProps {
  title?: string;
  message?: string;
  className?: string;
}

/**
 * Spinner Component v2 - FacturXPro Design System
 *
 * Spinner de chargement animé avec couleur Teal
 *
 * @example
 * ```tsx
 * <Spinner size="md" />
 * <Spinner size="lg" color="purple" />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Spinner"
 */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color = 'teal',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-b-2',
    lg: 'h-10 w-10 border-b-2',
  };

  const colorClasses = {
    teal: 'border-teal-600',
    purple: 'border-purple-600',
    gray: 'border-gray-600',
  };

  return (
    <div
      className={`
        animate-spin
        rounded-full
        ${sizeClasses[size]}
        ${colorClasses[color]}
        ${className}
      `.trim()}
    />
  );
};

/**
 * LoadingCard Component v2 - FacturXPro Design System
 *
 * Carte de chargement avec spinner et message
 * Utilisée pour afficher un état de chargement dans une Card
 *
 * @example
 * ```tsx
 * <LoadingCard
 *   title="Chargement des factures"
 *   message="Veuillez patienter..."
 * />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Loading Card"
 */
export const LoadingCard: React.FC<LoadingCardProps> = ({
  title,
  message,
  className = '',
}) => {
  const { t } = useTranslation();
  const displayTitle = title || t('common.loadingDefault');
  const displayMessage = message || t('common.pleaseWaitDefault');
  return (
    <Card variant="glassmorphism" padding="lg" className={className}>
      <div className="flex flex-col items-center justify-center py-12">
        <Spinner size="lg" />
        <h3 className="text-lg font-bold text-gray-900 mt-6 mb-2">{displayTitle}</h3>
        <p className="text-sm text-gray-500">{displayMessage}</p>
      </div>
    </Card>
  );
};

export default Spinner;
